import { paginateWithConcurrency } from 'query/autoPagination';
import { REVIEW_REFERENCES, tokenizeRoute } from 'query/routes';
import { getCsrfToken } from 'util/authenticityToken';
import { Document } from 'types';

/**
 * Example:
 *  request: /api/reviews/v1/reviews/123/references?ids[]=11111&ids[]=3333;
 *  response:
 *  {
 *    "references": [
 *      {
 *        "review_reference_id": 11111,
 *        "document": {
 *          "id": 987654,
 *          "url": "https://s3.amazonaws.com/....",
 *          "upload_file_name": "example.pdf",
 *          "uploaded_by": "John Doe",
 *          "is_primary": true,
 *          "created_at": "2024-06-10T12:34:56Z"
 *        }
 *      }
 *    ]
 *  }
 */
export type GetFilesResponse = {
  references: {
    review_reference_id: number;
    title: string | null;
    document_url: string | null;
    document: Document | null;
  }[];
};

export type GetFilesWithDocumentsResponse = {
  references: {
    review_reference_id: number;
    document: Document;
  }[];
};

export const getFiles = async (
  reviewId: string,
  referenceIds: number[],
  maxPageSize = 100
): Promise<GetFilesResponse> => {
  if (referenceIds.length === 0) {
    return { references: [] };
  }

  if (!reviewId) {
    throw new Error('reviewId is required');
  }

  const responses = await paginateWithConcurrency({
    items: referenceIds,
    options: {
      chunkSize: maxPageSize,
      concurrencyLimit: 3,
    },
    mapper: async (chunk) => {
      const url = tokenizeRoute(REVIEW_REFERENCES, { review_id: reviewId });
      const params = new URLSearchParams();
      chunk.forEach((id) => params.append('ids[]', id.toString()));

      return fetch(`${url}?${params.toString()}`).then((res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to fetch references for review ${reviewId}: ${res.statusText}`
          );
        }

        return res.json();
      });
    },
  });

  return { references: responses.flatMap((r) => r.references) };
};

/**
 * Wraps getFiles and filters out references with no uploaded documents.
 * Use this when you only need actual document objects, not document_url references.
 */
export const getFilesWithDocuments = async (
  reviewId: string,
  referenceIds: number[],
  maxPageSize = 100
): Promise<GetFilesWithDocumentsResponse> => {
  const response = await getFiles(reviewId, referenceIds, maxPageSize);
  return {
    references: response.references
      .filter((ref) => Boolean(ref.document?.id))
      .map((ref) => ({
        review_reference_id: ref.review_reference_id,
        document: ref.document as Document,
      })),
  };
};

export const getFileForReferenceUrl = (
  referenceUrl: string
): Promise<GetFilesWithDocumentsResponse> => {
  const reviewId = referenceUrl.match(/reviews\/(\d+)/)?.[1];
  const referenceId = referenceUrl.match(/review_references\/(\d+)/)?.[1];

  if (!reviewId || !referenceId) {
    return Promise.reject(new Error('Invalid reference URL: ' + referenceUrl));
  }

  return getFilesWithDocuments(reviewId as string, [Number(referenceId)]);
};

export const deleteFile = (fileId: string | number): Promise<Response> => {
  return fetch(`/documents/${fileId}`, {
    method: 'DELETE',
    headers: { 'X-CSRF-Token': getCsrfToken() },
    redirect: 'manual',
  });
};

export const unsetDocumentUrl = (referenceUrl: string): Promise<Response> => {
  return fetch(referenceUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
    body: JSON.stringify({ document_url: '' }),
  });
};

export const makeFilePrimary = (
  referenceUrl: string,
  fileId: string | number
): Promise<Response> => {
  return fetch(referenceUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
    body: JSON.stringify({ primary_document_id: fileId }),
  });
};

export const uploadFile = async (
  id: number,
  fileName: string,
  file: File,
  reviewId: string,
  onProgress: (progress: number) => void
): Promise<{ file: File; callbackUrl: string }> => {
  // Step 1: Get S3 upload configuration
  const params = new URLSearchParams({
    attachable_type: 'ReviewReference',
    attachable_id: String(id),
    file_name: fileName,
    review_id: reviewId,
  });

  const res = await fetch(`/documents/s3_upload_config?${params}`);
  if (!res.ok) {
    throw new Error(`Couldn't get S3 upload config`);
  }

  const { url, callback_url: callbackUrl } = await res.json();

  // Step 2: Upload file to S3
  await new Promise<{ file: File; callbackUrl: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);

    xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
      if (e.lengthComputable) {
        onProgress(e.loaded / e.total);
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ file, callbackUrl });
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.send(file);
  });

  // Step 3: Return file and callback URL
  return { file, callbackUrl };
};
